import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import OtpInput from "react-otp-input";
import {
  ArrowUpDown,
  CalendarDays,
  ChevronDown,
  Search,
  Send,
  ShieldCheck,
} from "lucide-react";

import { cn } from "../lib/utils";
import { useToast } from "../hooks/use-toast";
import { refreshPortfolioAndAssets } from "../services/digitalWalletService";
import { createZar8YieldPosition } from "../services/zar8YieldService";
import {
  formatFiatWithTwoDecimals,
  formatWithSignificantDigits,
  parseNumericValue,
} from "../utils/numberFormatter";
import {
  createDefaultZar8LockFlowState,
  calculateEstimatedYieldValue,
  emitZar8LockFlowEvent,
  getZar8DurationOption,
  getZar8LockPeriodFromDurationId,
  getZar8YieldAssetOption,
  sanitizeZar8AmountInput,
  ZAR8_LOCK_DURATION_OPTIONS,
  ZAR8_LOCK_FLOW_STEPS,
  ZAR8_YIELD_FLOW_TYPES,
  ZAR8_YIELD_ASSET_OPTIONS,
} from "../utils/zar8LockFlow";
import { SecuredFooter } from "./ui/secured-footer";
import {
  BackButton,
  BannerField,
  ContinueButton,
  FieldLabel,
  FlowModePill,
  LargeGhostButton,
  LargeGradientButton,
  SecondaryField,
  StepShell,
  WizardCard,
  YieldAssetBadge,
  ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND,
} from "./ui/zar8-yield-flow-ui";
import TwoFA from "./MainTwoFA";

const PROCESSING_STEP_DELAY_MS = 1600;

const invalidateZar8LockQueries = (queryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ["availableBalances"] }),
    queryClient.invalidateQueries({ queryKey: ["ledgerEntries"] }),
    queryClient.invalidateQueries({ queryKey: ["pendingLedgers"] }),
    queryClient.invalidateQueries({ queryKey: ["investPortfolio"] }),
    queryClient.invalidateQueries({ queryKey: ["portfolioEstimateAssets"] }),
    queryClient.invalidateQueries({ queryKey: ["userTransactionHistory"] }),
    queryClient.invalidateQueries({ queryKey: ["zar8YieldPositions"] }),
    queryClient.invalidateQueries({ queryKey: ["zar8YieldHistory"] }),
    queryClient.invalidateQueries({ queryKey: ["zar8YieldEarnings"] }),
  ]);

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallbackMessage;

const formatAssetAmount = (value, symbol) => {
  const numericValue = parseNumericValue(value);

  return `${formatWithSignificantDigits(numericValue, {
    minSignificant: 4,
    maxDecimals: 8,
  })} ${symbol}`;
};

function AssetOptionIcon({ option }) {
  const isGoldAsset = option.id === "xaut";

  return (
    <span
      className={cn(
        "flex h-10 w-10 items-center justify-center rounded-full text-lg font-semibold text-white",
        isGoldAsset
          ? "bg-[linear-gradient(180deg,#E8D38A_0%,#C8A94B_100%)]"
          : "bg-[#F7931A]"
      )}
    >
      {isGoldAsset ? "T" : "₿"}
    </span>
  );
}

function AssetPickerModal({
  searchQuery,
  onSearchChange,
  options,
  onSelect,
  onClose,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#060606]/20 px-4 backdrop-blur-[2px]">
      <div className="w-full max-w-[500px] rounded-[24px] border border-[#EBE9EF] bg-white px-10 pb-10 pt-6 shadow-[0_24px_80px_rgba(51,18,95,0.1)]">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold leading-6 text-[#423E51]">
            Select the asset you’d like to yield
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-2 text-sm font-semibold leading-5 text-[#EB3366] transition-colors hover:bg-[#FFF5F8]"
          >
            Close
          </button>
        </div>

        <div className="mt-8 flex h-14 items-center gap-3 rounded-full border border-[#EBE9EF] px-6">
          <Search className="h-3 w-3 text-[#6D6584]" />
          <input
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search"
            className="w-full bg-transparent text-sm leading-5 text-[#423E51] outline-none placeholder:text-[#6D6584]"
          />
        </div>

        <div className="mt-[18px] space-y-[9px]">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-label={`${option.label} ${option.subtitle}`}
              onClick={() => onSelect(option.id)}
              className="flex h-[88px] w-full items-center rounded-2xl border border-[#EBE9EF] px-4 text-left transition-colors hover:bg-[#FAF9FC]"
            >
              <AssetOptionIcon option={option} />
              <div className="ml-3 min-w-0">
                <p className="truncate text-[20px] font-semibold leading-6 text-[#423E51]">
                  {option.label}
                </p>
                <p className="mt-2 text-sm leading-5 text-[#6D6584]">
                  {option.subtitle}
                </p>
              </div>
            </button>
          ))}
          {options.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#EBE9EF] px-4 py-8 text-center text-sm leading-5 text-[#6D6584]">
              No matching assets found.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DurationPicker({ selectedDurationId, onSelect, onClose, className }) {
  return (
    <div
      className={cn(
        "max-h-[min(420px,calc(100vh-96px))] overflow-y-auto rounded-2xl border border-[#EBE9EF] bg-white py-2 shadow-[0_24px_80px_rgba(51,18,95,0.08)]",
        className
      )}
    >
      {ZAR8_LOCK_DURATION_OPTIONS.map((option) => {
        const isSelected = option.id === selectedDurationId;

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => {
              onSelect(option.id);
              onClose();
            }}
            className="flex w-full items-center gap-4 px-6 py-4 text-left transition-colors hover:bg-[#FAF9FC]"
          >
            <span
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border",
                isSelected
                  ? "border-[2px] border-[#FF315F]"
                  : "border-[#CECBD7] p-[2px]"
              )}
            >
              {isSelected ? (
                <span className="h-5 w-5 rounded-full bg-[#FF315F]" />
              ) : null}
            </span>
            <span
              className={cn(
                "text-base font-semibold leading-6",
                isSelected ? "text-[#423E51]" : "text-[#6D6584]"
              )}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SummaryRow({ label, value, accent = false }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-[#EBE9EF] px-6 py-5">
      <p className="text-sm leading-5 text-[#6D6584]">{label}</p>
      <p
        className={cn(
          "text-base font-semibold leading-6 text-[#423E51]",
          accent && "text-[#57249C]"
        )}
      >
        {value}
      </p>
    </div>
  );
}

function ProcessingStep() {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white">
      <div className="relative flex min-h-[642px] items-center justify-center">
        <div className="absolute inset-0">
          <div className="absolute left-1/2 top-1/2 h-[410px] w-[410px] -translate-x-[55%] -translate-y-[58%] rounded-full bg-[#7D2DB7]/35 blur-[90px]" />
          <div className="absolute left-1/2 top-1/2 h-[360px] w-[360px] -translate-x-[15%] -translate-y-[58%] rounded-full bg-[#EB3366]/35 blur-[100px]" />
          <div className="absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-[5%] -translate-y-[-5%] rounded-full bg-[#00D9FF]/28 blur-[110px]" />
        </div>
        <div className="relative flex flex-col items-center">
          <span className="text-[48px] font-semibold leading-none tracking-[-0.04em] text-white">
            80eight
          </span>
        </div>
      </div>
    </div>
  );
}

function SuccessIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]">
      <span className="absolute left-[19px] top-[16px] h-[48px] w-[48px] rounded-full bg-[#F2E9FB]" />
      <span className="absolute left-[154px] top-[7px] h-[10px] w-[10px] rounded-full bg-[#00E2FF]" />
      <span className="absolute left-[140px] top-[76px] text-[34px] leading-none text-[#D342A9]">
        ✦
      </span>
      <div className="absolute left-[48px] top-[18px] rotate-[-12deg] text-[#E53672]">
        <Send className="h-[92px] w-[92px] stroke-[1.5]" />
      </div>
    </div>
  );
}

function SuccessStep({ onDone, yieldAssetLabel }) {
  return (
    <div className="overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white pt-5">
      <div className="mx-auto flex w-full max-w-[709px] flex-col items-center gap-16 px-6 pb-[122px] pt-[74px] text-center">
        <div className="flex flex-col items-center gap-12">
          <SuccessIllustration />
          <div className="flex max-w-[358px] flex-col gap-6">
            <p className="text-[32px] font-semibold leading-10 text-[#57249C]">
              Lock Successful!
            </p>
            <p className="text-[20px] leading-8 text-[#423E51]">
              Your ZAR8 is now locked and will begin earning {yieldAssetLabel}{" "}
              yield.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onDone}
          className="h-12 w-full max-w-[302px] rounded-full text-sm font-semibold leading-5 text-white"
          style={{ background: ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

export default function Zar8LockFlow({
  flowState,
  onFlowStateChange,
  availableBalanceZar,
  zar8Asset,
  isTwoFactorEnabled,
  onClose,
  onModeChange,
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [amountError, setAmountError] = useState("");
  const [configurationError, setConfigurationError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isAssetPickerOpen, setIsAssetPickerOpen] = useState(false);
  const [isDurationPickerOpen, setIsDurationPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorReadyOverride, setTwoFactorReadyOverride] = useState(false);

  const availableBalance = parseNumericValue(availableBalanceZar);
  const amountValue = parseNumericValue(flowState.amountInput);
  const amountDisplay = formatFiatWithTwoDecimals(amountValue);
  const assetDisplay = formatAssetAmount(amountValue, "ZAR8");
  const selectedYieldAsset = getZar8YieldAssetOption(
    flowState.selectedYieldAssetId
  );
  const selectedDuration = getZar8DurationOption(flowState.selectedDurationId);
  const estimatedYield = useMemo(
    () =>
      calculateEstimatedYieldValue({
        amountZar: amountValue,
        assetId: flowState.selectedYieldAssetId,
        durationId: flowState.selectedDurationId,
      }),
    [amountValue, flowState.selectedDurationId, flowState.selectedYieldAssetId]
  );
  const filteredYieldAssets = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return ZAR8_YIELD_ASSET_OPTIONS;
    }

    return ZAR8_YIELD_ASSET_OPTIONS.filter((option) =>
      `${option.label} ${option.subtitle}`
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [searchQuery]);
  const isTwoFactorReady = isTwoFactorEnabled || twoFactorReadyOverride;
  const hasOtpError = Boolean(otpError);

  useEffect(() => {
    if (flowState.step !== ZAR8_LOCK_FLOW_STEPS.processing) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      emitZar8LockFlowEvent("zar8_lock_success", {
        amount: amountValue,
        durationId: flowState.selectedDurationId,
        yieldAssetId: flowState.selectedYieldAssetId,
      });
      onFlowStateChange((currentState) => ({
        ...currentState,
        step: ZAR8_LOCK_FLOW_STEPS.success,
      }));
    }, PROCESSING_STEP_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [
    amountValue,
    flowState.selectedDurationId,
    flowState.selectedYieldAssetId,
    flowState.step,
    onFlowStateChange,
  ]);

  const updateFlowState = (updater) => {
    onFlowStateChange((currentState) => {
      const nextState =
        typeof updater === "function" ? updater(currentState) : updater;

      return {
        ...currentState,
        ...nextState,
      };
    });
  };

  const handleBack = () => {
    setAmountError("");
    setConfigurationError("");
    setOtpError("");

    if (flowState.step === ZAR8_LOCK_FLOW_STEPS.amount) {
      onClose();
      return;
    }

    if (flowState.step === ZAR8_LOCK_FLOW_STEPS.configuration) {
      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.amount });
      return;
    }

    if (flowState.step === ZAR8_LOCK_FLOW_STEPS.summary) {
      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.configuration });
      return;
    }

    if (flowState.step === ZAR8_LOCK_FLOW_STEPS.twoFactor) {
      if (showTwoFactorSetup) {
        setShowTwoFactorSetup(false);
        return;
      }

      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.summary });
    }
  };

  const handleAmountContinue = () => {
    if (amountValue <= 0) {
      setAmountError("Enter a valid amount to continue.");
      return;
    }

    if (amountValue > availableBalance) {
      setAmountError("Amount exceeds your available ZAR8 balance.");
      return;
    }

    setAmountError("");
    emitZar8LockFlowEvent("zar8_lock_initiated", {
      amount: amountValue,
      amountMode: flowState.amountMode,
    });
    updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.configuration });
  };

  const handleConfigurationContinue = () => {
    if (!selectedYieldAsset || !selectedDuration) {
      setConfigurationError("Select a yield asset and duration to continue.");
      return;
    }

    setConfigurationError("");
    updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.summary });
  };

  const handleSummaryContinue = () => {
    emitZar8LockFlowEvent("zar8_lock_confirmed", {
      amount: amountValue,
      durationId: flowState.selectedDurationId,
      yieldAssetId: flowState.selectedYieldAssetId,
    });
    updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.twoFactor });
  };

  const handleOtpVerify = async () => {
    if (otpCode.length !== 6) {
      setOtpError("Enter the 6-digit verification code to continue.");
      return;
    }

    const lockPeriod = getZar8LockPeriodFromDurationId(
      flowState.selectedDurationId
    );

    if (!lockPeriod) {
      setOtpError("Select a valid lock duration to continue.");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      setOtpError("");
      await createZar8YieldPosition({
        zar8Amount: amountValue,
        lockPeriod,
        twoFactorToken: otpCode,
      });
      await refreshPortfolioAndAssets();
      await invalidateZar8LockQueries(queryClient);
      updateFlowState({ step: ZAR8_LOCK_FLOW_STEPS.processing });
    } catch (error) {
      emitZar8LockFlowEvent("zar8_lock_failure", {
        reason: "lock_submission_failed",
      });
      const message = getApiErrorMessage(
        error,
        "We couldn’t lock your ZAR8. Try again."
      );
      setOtpError(message);
      toast({
        description: message,
        variant: "eighty",
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleClose = () => {
    emitZar8LockFlowEvent("zar8_lock_abandoned", {
      step: flowState.step,
    });
    onClose();
  };

  const handleDone = () => {
    onClose();
  };

  const handleModeChange = (nextFlowType) => {
    if (nextFlowType === ZAR8_YIELD_FLOW_TYPES.lock) {
      return;
    }

    if (onModeChange) {
      onModeChange(nextFlowType);
      return;
    }

    onFlowStateChange({
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: nextFlowType,
    });
  };

  const topAsset = {
    label: "ZAR8",
    icon: zar8Asset?.assetIcon || zar8Asset?.image || "",
  };

  return (
    <StepShell>
      {isAssetPickerOpen ? (
        <AssetPickerModal
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          options={filteredYieldAssets}
          onSelect={(assetId) => {
            updateFlowState({ selectedYieldAssetId: assetId });
            setIsAssetPickerOpen(false);
            setSearchQuery("");
            setConfigurationError("");
          }}
          onClose={() => {
            setIsAssetPickerOpen(false);
            setSearchQuery("");
          }}
        />
      ) : null}

      {showTwoFactorSetup ? (
        <div className="overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white">
          <TwoFA
            closeOverlay={() => setShowTwoFactorSetup(false)}
            isSetUp={() => {
              setShowTwoFactorSetup(false);
              setTwoFactorReadyOverride(true);
            }}
            on2FAEnabled={() => setTwoFactorReadyOverride(true)}
            onDoneClick={() => {
              setShowTwoFactorSetup(false);
              setTwoFactorReadyOverride(true);
            }}
            className="rounded-none border-none shadow-none"
          />
        </div>
      ) : null}

      {!showTwoFactorSetup && flowState.step === ZAR8_LOCK_FLOW_STEPS.amount ? (
        <WizardCard
          title="Lock ZAR8"
          onCancel={handleClose}
          className="overflow-visible"
          footer={
            <SecuredFooter className="mt-0 w-full border-[#EBE9EF] pb-8 pt-6" />
          }
        >
          <FlowModePill
            currentMode={ZAR8_YIELD_FLOW_TYPES.lock}
            onChange={handleModeChange}
          />

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <FieldLabel>Asset</FieldLabel>
              <SecondaryField>
                <div className="flex items-center gap-2">
                  <YieldAssetBadge assetIcon={topAsset?.icon} />
                  <p className="text-base font-semibold leading-6 text-[#423E51]">
                    ZAR8
                  </p>
                </div>
              </SecondaryField>
            </div>

            <div className="space-y-3">
              <FieldLabel>Available balance</FieldLabel>
              <BannerField>
                <p className="text-[20px] font-semibold leading-6 text-[#6D6584]">
                  {formatFiatWithTwoDecimals(availableBalance)}
                </p>
              </BannerField>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <FieldLabel>Enter amount to lock</FieldLabel>
            <SecondaryField className="flex-wrap gap-4 py-5">
              <div className="min-w-[170px] flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[24px] font-semibold leading-8 text-[#33125F]">
                    {flowState.amountMode === "zar" ? "R" : ""}
                  </span>
                  <input
                    value={flowState.amountInput}
                    onChange={(event) => {
                      setAmountError("");
                      updateFlowState({
                        amountInput: sanitizeZar8AmountInput(
                          event.target.value
                        ),
                      });
                    }}
                    placeholder="0.00"
                    inputMode="decimal"
                    className="min-w-0 flex-1 bg-transparent text-[24px] font-semibold leading-8 text-[#33125F] outline-none placeholder:text-[#9D96AF]"
                  />
                  {flowState.amountMode === "zar8" ? (
                    <span className="text-[24px] font-semibold leading-8 text-[#33125F]">
                      ZAR8
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="ml-auto flex items-center gap-2">
                <p className="text-base leading-6 text-[#6D6584]">
                  ≈
                  {flowState.amountMode === "zar"
                    ? assetDisplay
                    : amountDisplay.replace(/^R /, " R ")}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    updateFlowState((currentState) => ({
                      amountMode:
                        currentState.amountMode === "zar" ? "zar8" : "zar",
                    }));
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
                >
                  <ArrowUpDown className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAmountError("");
                    updateFlowState({
                      amountInput: availableBalance.toFixed(2),
                    });
                  }}
                  className="flex h-10 items-center justify-center rounded-full border border-[#EBE9EF] px-4 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
                >
                  Max
                </button>
              </div>
            </SecondaryField>
            {amountError ? (
              <p className="text-sm font-medium leading-5 text-[#DA2F58]">
                {amountError}
              </p>
            ) : null}
          </div>

          <div className="mt-7 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <BackButton onClick={handleBack} ariaLabel="Back to dashboard" />
            <ContinueButton onClick={handleAmountContinue}>
              Continue
            </ContinueButton>
          </div>
        </WizardCard>
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.configuration ? (
        <WizardCard
          title="Lock ZAR8"
          onCancel={handleClose}
          footer={
            <SecuredFooter className="mt-0 w-full border-[#EBE9EF] pb-8 pt-6" />
          }
        >
          <FlowModePill
            currentMode={ZAR8_YIELD_FLOW_TYPES.lock}
            onChange={handleModeChange}
          />

          <div className="mt-6 space-y-6">
            <BannerField>
              <p className="w-full text-center text-[24px] font-semibold leading-8 text-[#423E51]">
                {amountDisplay}
              </p>
            </BannerField>

            <div className="space-y-3">
              <FieldLabel>Yield Asset</FieldLabel>
              <div className="relative">
                <SecondaryField className="justify-between">
                  <button
                    type="button"
                    aria-label="Open yield asset selector"
                    onClick={() => setIsAssetPickerOpen(true)}
                    className="flex w-full items-center justify-between gap-4 text-left"
                  >
                    <div className="flex items-center gap-2">
                      {selectedYieldAsset ? (
                        <AssetOptionIcon option={selectedYieldAsset} />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F6F3FA] text-[#9D96AF]">
                          <ShieldCheck className="h-5 w-5" />
                        </span>
                      )}
                      <div>
                        <p
                          className={cn(
                            "text-base font-semibold leading-6",
                            selectedYieldAsset
                              ? "text-[#423E51]"
                              : "text-[#6D6584]"
                          )}
                        >
                          {selectedYieldAsset?.label || "Select..."}
                        </p>
                        {selectedYieldAsset ? (
                          <p className="mt-1 text-sm leading-5 text-[#6D6584]">
                            {selectedYieldAsset.subtitle}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584]">
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  </button>
                </SecondaryField>
              </div>
            </div>

            <div className="space-y-3">
              <FieldLabel>Select duration</FieldLabel>
              <div className="relative">
                <SecondaryField className="justify-between">
                  <button
                    type="button"
                    aria-label="Open lock duration selector"
                    onClick={() =>
                      setIsDurationPickerOpen((currentValue) => !currentValue)
                    }
                    className="flex w-full items-center justify-between gap-4 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F6F3FA] text-[#6D6584]">
                        <CalendarDays className="h-5 w-5" />
                      </span>
                      <p className="text-base font-semibold leading-6 text-[#423E51]">
                        {selectedDuration?.label || "Select Duration"}
                      </p>
                    </div>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 text-[#6D6584] transition-transform",
                        isDurationPickerOpen && "rotate-180"
                      )}
                    />
                  </button>
                </SecondaryField>

                {isDurationPickerOpen ? (
                  <DurationPicker
                    className="absolute bottom-[calc(100%+12px)] left-0 right-0 z-20"
                    selectedDurationId={flowState.selectedDurationId}
                    onSelect={(durationId) => {
                      setConfigurationError("");
                      updateFlowState({ selectedDurationId: durationId });
                    }}
                    onClose={() => setIsDurationPickerOpen(false)}
                  />
                ) : null}
              </div>
            </div>

            {selectedYieldAsset && selectedDuration ? (
              <div className="rounded-2xl border border-[#EBE9EF] bg-[#FAF9FC] px-6 py-5">
                <p className="text-sm leading-5 text-[#6D6584]">
                  Estimated yield
                </p>
                <p className="mt-2 text-2xl font-semibold leading-8 text-[#33125F]">
                  {formatFiatWithTwoDecimals(estimatedYield)}
                </p>
                <p className="mt-2 text-sm leading-5 text-[#6D6584]">
                  Based on {selectedYieldAsset.label} and{" "}
                  {selectedDuration.label.toLowerCase()} lock settings.
                </p>
              </div>
            ) : null}

            {configurationError ? (
              <p className="text-sm font-medium leading-5 text-[#DA2F58]">
                {configurationError}
              </p>
            ) : null}
          </div>

          <div className="mt-7 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <BackButton onClick={handleBack} />
            <ContinueButton onClick={handleConfigurationContinue}>
              Continue
            </ContinueButton>
          </div>
        </WizardCard>
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.summary ? (
        <WizardCard
          title="Review lock details"
          onCancel={handleClose}
          footer={
            <SecuredFooter className="mt-0 w-full border-[#EBE9EF] pb-8 pt-6" />
          }
        >
          <div className="mt-10 space-y-4">
            <BannerField>
              <p className="w-full text-center text-[24px] font-semibold leading-8 text-[#423E51]">
                {amountDisplay}
              </p>
            </BannerField>
            <SummaryRow label="Amount to lock" value={amountDisplay} accent />
            <SummaryRow
              label="Yield asset"
              value={selectedYieldAsset?.label || "-"}
            />
            <SummaryRow
              label="Lock duration"
              value={selectedDuration?.label || "-"}
            />
            <SummaryRow
              label="Estimated yield"
              value={formatFiatWithTwoDecimals(estimatedYield)}
            />
            <SummaryRow label="Fees" value="R 0.00" />
            <SummaryRow label="Total locked" value={amountDisplay} accent />
          </div>

          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <BackButton onClick={handleBack} />
            <ContinueButton onClick={handleSummaryContinue}>
              Confirm Lock
            </ContinueButton>
          </div>
        </WizardCard>
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.twoFactor ? (
        <WizardCard
          title="Confirm Yield"
          onCancel={handleClose}
          onHeaderBack={handleBack}
          footer={
            <SecuredFooter className="mt-0 w-full border-[#EBE9EF] pb-8 pt-6" />
          }
        >
          {isTwoFactorReady ? (
            <div className="mt-10">
              <div className="mx-auto w-full max-w-[603px]">
                <div className="flex flex-col gap-6">
                  <p className="text-[32px] font-semibold leading-10 text-[#57249C]">
                    Enable 2-Factor Authentication
                  </p>
                  <p className="max-w-[560px] text-[20px] leading-8 text-[#423E51]">
                    Enter the 6-digit verification code generated by your
                    authentication app:
                  </p>
                </div>

                <div className="mt-12 flex justify-center">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-[20px] text-[#EBE9EF]">
                      -
                    </span>
                    <OtpInput
                      value={otpCode}
                      onChange={(value) => {
                        setOtpError("");
                        setOtpCode(value);
                      }}
                      numInputs={6}
                      shouldAutoFocus
                      containerStyle={{
                        gap: "12px",
                        alignItems: "center",
                      }}
                      renderInput={(props, index) => (
                        <input
                          {...props}
                          aria-label={`2FA digit ${index + 1}`}
                          style={{
                            ...(props.style || {}),
                            width: "56px",
                            minWidth: "56px",
                            maxWidth: "56px",
                            height: "56px",
                          }}
                          className={cn(
                            "h-14 w-14 rounded-2xl border bg-white text-center text-[36px] font-normal leading-[44px] outline-none transition-colors",
                            hasOtpError
                              ? "border-[#DA2F58] text-[#DA2F58]"
                              : index === 0
                                ? "border-2 border-[#57249C] text-[#6D6584]"
                                : "border-[#CECBD7] text-[#6D6584] focus:border-[#57249C]",
                            index === 2 && "mr-8",
                            index === 3 && "ml-8"
                          )}
                        />
                      )}
                    />
                  </div>
                </div>

                {otpError ? (
                  <p className="mt-5 text-center text-sm font-medium leading-5 text-[#DA2F58]">
                    {otpError}
                  </p>
                ) : null}

                <div className="mt-10 grid gap-3 sm:grid-cols-2">
                  <LargeGhostButton onClick={handleClose}>
                    Cancel
                  </LargeGhostButton>
                  <LargeGradientButton
                    onClick={handleOtpVerify}
                    disabled={isVerifyingOtp}
                  >
                    {isVerifyingOtp ? "Verifying..." : "Verify and activate"}
                  </LargeGradientButton>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-10 pb-2 text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#F8F4FD] text-[#57249C]">
                <ShieldCheck className="h-9 w-9" />
              </div>
              <p className="mt-8 text-2xl font-semibold leading-8 text-[#33125F]">
                Enable 2FA to continue
              </p>
              <p className="mx-auto mt-4 max-w-[452px] text-base leading-6 text-[#6D6584]">
                ZAR8 Yield locks require 2-factor authentication before funds
                can be secured. Enable 2FA and return to this step to complete
                the transaction.
              </p>

              <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <BackButton onClick={handleBack} />
                <ContinueButton onClick={() => setShowTwoFactorSetup(true)}>
                  Enable 2FA
                </ContinueButton>
              </div>
            </div>
          )}
        </WizardCard>
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.processing ? (
        <ProcessingStep />
      ) : null}

      {!showTwoFactorSetup &&
      flowState.step === ZAR8_LOCK_FLOW_STEPS.success ? (
        <SuccessStep
          onDone={handleDone}
          yieldAssetLabel={selectedYieldAsset?.label || "your selected asset"}
        />
      ) : null}
    </StepShell>
  );
}
